import React, { useEffect, useRef, useState } from "react";

// Model-viewer is loaded lazily from the official UMD bundle (`.min.js`) —
// this ships `three` internally, avoiding webpack peer-dep resolution
// problems that occur when importing the ESM entry from `@google/model-viewer`.
const MODEL_VIEWER_SRC = "https://cdn.jsdelivr.net/npm/@google/model-viewer/dist/model-viewer.min.js";
const loadModelViewer = () => {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.customElements && window.customElements.get("model-viewer")) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-mv]`);
    if (existing) { existing.addEventListener("load", resolve); return; }
    const s = document.createElement("script");
    s.type = "module";
    s.src = MODEL_VIEWER_SRC;
    s.dataset.mv = "1";
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
};

// Reference photography — 5 real angles of the actual SwissAlpenLine BYD
// Seal U DM-i. Drag to scrub between them; hotspots overlay meaningful
// spots on the vehicle regardless of the visible frame.
const FRAMES = [
  { src: "/images/byd-front-lucerne.jpg",   label: "Front · design"      },
  { src: "/images/byd-lucerne-hero.jpg",    label: "Executive presence"  },
  { src: "/images/byd-mountain-road.jpg",   label: "Alpine side profile" },
  { src: "/images/byd-burgenstock.jpg",     label: "Hotel arrival"       },
  { src: "/images/byd-rear-highway.jpg",    label: "Rear · design"       },
];

// Hotspots — expressed as (x%, y%) relative to the front-facing frame.
// They fade as the user rotates further from frame 0.
const HOTSPOTS = [
  { id: "front",    x: 42, y: 55, title: "Front design",      body: "Executive front fascia, matrix LED headlights, discreet chrome accents." },
  { id: "hybrid",   x: 40, y: 78, title: "Plug-in hybrid",    body: "DM-i platform · silent EV mode in the city, hybrid 1'000+ km range across borders." },
  { id: "interior", x: 68, y: 52, title: "Interior",          body: "Nappa leather, 12-way heated & ventilated seats, panoramic glass roof." },
  { id: "boot",     x: 88, y: 62, title: "Boot space",        body: "552 L trunk (1'440 L with rear seats folded) — luggage, ski bags, delivery cases." },
];

const CAPTION = {
  en: "SwissAlpenLine operates a BYD Seal U DM-i — modern executive comfort without unnecessary extravagance.",
  de: "SwissAlpenLine fährt einen BYD Seal U DM-i — moderner Executive-Komfort ohne unnötige Extravaganz.",
  pt: "A SwissAlpenLine opera um BYD Seal U DM-i — conforto executivo moderno, sem extravagância desnecessária.",
  fr: "SwissAlpenLine roule en BYD Seal U DM-i — confort exécutif moderne, sans extravagance inutile.",
  it: "SwissAlpenLine viaggia su BYD Seal U DM-i — comfort executive moderno, senza extravaganza inutile.",
};

/**
 * Premium 3D-style vehicle viewer.
 *
 * Drops a real <model-viewer> element pointing at `/models/byd-seal-u.glb`
 * when a GLB is available. Falls back — automatically — to a drag-to-scrub
 * photo carousel using the 5 branded reference frames when no GLB is
 * present. Either way, a shared hotspot overlay explains key vehicle
 * features. Zoom + inertia + touch drag come for free from <model-viewer>;
 * the fallback re-implements drag+wheel by hand.
 */
export const Vehicle3DViewer = ({ lang = "en", glbPath = "/models/byd-seal-u.glb" }) => {
  const [hasGlb, setHasGlb] = useState(null);
  const [frame, setFrame] = useState(0);          // 0..FRAMES.length-1
  const [openHotspot, setOpenHotspot] = useState(null);
  const wrapRef = useRef(null);
  const dragRef = useRef({ dragging: false, startX: 0, startFrame: 0 });

  // Detect whether the .glb exists. SPA fallback would return `index.html`
  // (200 + text/html) for a missing static file, so we additionally require
  // the response Content-Type to look like a binary model.
  useEffect(() => {
    let mounted = true;
    fetch(glbPath, { method: "HEAD" })
      .then((r) => {
        if (!mounted) return;
        const ct = (r.headers.get("content-type") || "").toLowerCase();
        const looksLikeGlb = r.ok && (
          ct.includes("gltf") ||
          ct.includes("octet-stream") ||
          ct.includes("application/binary") ||
          ct === ""
        ) && !ct.includes("html");
        if (looksLikeGlb) {
          loadModelViewer().then(() => mounted && setHasGlb(true)).catch(() => mounted && setHasGlb(false));
        } else {
          setHasGlb(false);
        }
      })
      .catch(() => mounted && setHasGlb(false));
    return () => { mounted = false; };
  }, [glbPath]);

  // Fallback drag handlers — horizontal drag scrubs between photo frames.
  // Guard: ignore pointer events that originate on interactive children
  // (frame dots / hotspots), otherwise setPointerCapture would swallow the
  // pointerup and the button's onClick would never fire.
  const onPointerDown = (e) => {
    if (hasGlb) return;
    if (e.target && e.target.closest && e.target.closest("button")) return;
    dragRef.current = { dragging: true, startX: e.clientX ?? e.touches?.[0]?.clientX, startFrame: frame };
    wrapRef.current?.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.dragging) return;
    const x = e.clientX ?? e.touches?.[0]?.clientX;
    const dx = x - dragRef.current.startX;
    const w = wrapRef.current?.offsetWidth || 800;
    // 1/4 of the viewer width = 1 frame change.
    const delta = Math.round(dx / (w / (FRAMES.length * 1.2)));
    let nf = dragRef.current.startFrame - delta;
    nf = ((nf % FRAMES.length) + FRAMES.length) % FRAMES.length;
    if (nf !== frame) setFrame(nf);
  };
  const onPointerUp = () => { dragRef.current.dragging = false; };

  const caption = CAPTION[lang] || CAPTION.en;
  const gold = "#B08D2F";

  return (
    <div className="w-full" data-testid="vehicle-3d-viewer">
      <div
        ref={wrapRef}
        className="relative w-full bg-black border border-brand-border overflow-hidden select-none"
        style={{ aspectRatio: "16 / 10", touchAction: hasGlb ? "auto" : "pan-y" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {/* Subtle radial vignette + gold glow for premium feel */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(circle at 50% 55%, rgba(176,141,47,0.10), transparent 60%), radial-gradient(circle at 50% 50%, transparent 40%, rgba(0,0,0,0.55) 100%)" }}
        />

        {hasGlb === true && (
          // Real 3D model — full rotate + zoom + inertia + touch out of the box.
          <model-viewer
            src={glbPath}
            alt="BYD Seal U DM-i"
            camera-controls
            auto-rotate
            auto-rotate-delay="4000"
            rotation-per-second="20deg"
            interaction-prompt="none"
            shadow-intensity="1.2"
            exposure="0.9"
            environment-image="neutral"
            style={{ width: "100%", height: "100%", backgroundColor: "transparent" }}
          />
        )}

        {hasGlb === false && (
          // Fallback — drag-to-scrub photo carousel with cross-fade.
          <>
            {FRAMES.map((f, i) => (
              <img
                key={f.src}
                src={f.src}
                alt={f.label}
                draggable={false}
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-out"
                style={{ opacity: i === frame ? 1 : 0 }}
              />
            ))}
            {/* Frame indicator dots */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2" data-testid="frame-indicators">
              {FRAMES.map((f, i) => (
                <button
                  key={f.src}
                  type="button"
                  onClick={() => setFrame(i)}
                  data-testid={`frame-${i}`}
                  aria-label={f.label}
                  className="w-8 h-[3px] transition-all"
                  style={{ background: i === frame ? gold : "rgba(255,255,255,0.22)" }}
                />
              ))}
            </div>
            {/* Drag hint — only until first interaction */}
            <div className="absolute top-6 left-1/2 -translate-x-1/2 text-[10px] tracking-widest2 uppercase text-brand-silver bg-black/40 px-3 py-1 border border-brand-border">
              drag or tap dots to rotate
            </div>
          </>
        )}

        {/* Hotspots — always overlaid, dim when the current frame is far
            from the front so they don't cover unrelated angles. */}
        {hasGlb !== null && HOTSPOTS.map((h) => {
          const dim = hasGlb === false ? Math.abs(frame) > 0 ? 0.35 : 1 : 1;
          return (
            <button
              key={h.id}
              type="button"
              onClick={() => setOpenHotspot(openHotspot === h.id ? null : h.id)}
              data-testid={`hotspot-${h.id}`}
              className="absolute -translate-x-1/2 -translate-y-1/2 group"
              style={{ left: `${h.x}%`, top: `${h.y}%`, opacity: dim, transition: "opacity 400ms" }}
              aria-label={h.title}
            >
              <span className="block w-3 h-3 rounded-full border-2 border-white/80 bg-transparent group-hover:scale-125 transition-transform"
                    style={{ boxShadow: `0 0 0 6px rgba(176,141,47,0.20)` }} />
              {openHotspot === h.id && (
                <div className="absolute left-4 top-4 w-56 border border-brand-border bg-black/85 backdrop-blur-sm p-3 text-left z-10">
                  <p className="text-[10px] tracking-widest2 uppercase mb-1" style={{ color: gold }}>— {h.title}</p>
                  <p className="text-xs text-brand-silver leading-relaxed">{h.body}</p>
                </div>
              )}
            </button>
          );
        })}

        {/* Static loader */}
        {hasGlb === null && (
          <div className="absolute inset-0 flex items-center justify-center text-brand-dim text-[10px] tracking-widest2 uppercase">
            loading vehicle…
          </div>
        )}
      </div>

      {/* Caption — same across GLB and fallback */}
      <p className="mt-6 text-brand-silver text-sm md:text-base leading-relaxed max-w-3xl">
        {caption}
      </p>
      {hasGlb === false && (
        <p className="mt-2 text-[10px] tracking-widest2 uppercase text-brand-dim">
          Interactive preview · full 3D model coming soon (drop `/public/models/byd-seal-u.glb`)
        </p>
      )}
    </div>
  );
};

export default Vehicle3DViewer;
